import { useState, useMemo, useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';

import {
  useGetSchDetailsQuery,
  usePostChangeSchMutation,
  useGetAllDistrictListQuery,
  useGetEduDistQuery,
  useGetDisBlockQuery,
  useGetManagementQuery,
  useGetSchCategoryQuery,
} from './counselling.services';
import { alertService } from '../../shared/services/alertService';
import {
  SCHOOL_TYPE_LIST,
  TYPE_LIST,
  SCH_TYPE_CATEGORY_LIST,
} from '../../shared/services/defaultValues';

/**
 * Migrated from Angular `ChangeSchoolInformationComponent`
 * (src/app/views/pages/counselling/change-school-information).
 *
 * Route: /counselling/school-info
 *
 * Uses Formik for edit-mode form state (mirrors Angular's Reactive FormGroup).
 */

// ── Dropdown option shape ────────────────────────────────────────────────────

interface Option {
  label: string;
  value: string;
}

// ── Form values shape ────────────────────────────────────────────────────────

interface SchoolFormValues {
  DistId: string;
  EduDisId: string;
  BlkId: string;
  SchlTypeId: string;
  MngId: string;
  CateId: string;
  CatyId: string;
  Type: string;
  AeoUser: string;
  HillRot: boolean;
  DisNme: string;
  EduDisNme: string;
  BlkNme: string;
  SchlType: string;
  MngNme: string;
  CateNme: string;
  CatyNme: string;
}

const INITIAL_VALUES: SchoolFormValues = {
  DistId: '',
  EduDisId: '',
  BlkId: '',
  SchlTypeId: '',
  MngId: '',
  CateId: '',
  CatyId: '',
  Type: '',
  AeoUser: '',
  HillRot: false,
  DisNme: '',
  EduDisNme: '',
  BlkNme: '',
  SchlType: '',
  MngNme: '',
  CateNme: '',
  CatyNme: '',
};

// ── Component ────────────────────────────────────────────────────────────────

const ChangeSchoolInfo = () => {
  const [udise, setUdise] = useState('');
  const [submittedUdise, setSubmittedUdise] = useState<string | null>(null);
  const [isViewSchDet, setIsViewSchDet] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: schRaw, refetch: refetchSchDetails } = useGetSchDetailsQuery(
    { udiseCode: submittedUdise },
    { skip: !submittedUdise }
  );
  const [postChangeSch] = usePostChangeSchMutation();
  const { data: districtRaw } = useGetAllDistrictListQuery();
  const { data: managementRaw } = useGetManagementQuery();
  const { data: schCategoryRaw } = useGetSchCategoryQuery();

  // ── Formik ───────────────────────────────────────────────────────────────

  const schForm = useFormik<SchoolFormValues>({
    initialValues: INITIAL_VALUES,
    validate: (values) => {
      const errors: Partial<Record<keyof SchoolFormValues, string>> = {};
      if (!values.DistId) errors.DistId = 'Required';
      if (!values.EduDisId) errors.EduDisId = 'Required';
      if (!values.BlkId) errors.BlkId = 'Required';
      if (!values.SchlTypeId) errors.SchlTypeId = 'Required';
      if (!values.MngId) errors.MngId = 'Required';
      if (!values.CateId) errors.CateId = 'Required';
      if (!values.CatyId) errors.CatyId = 'Required';
      if (!values.Type) errors.Type = 'Required';
      if (!values.AeoUser) errors.AeoUser = 'Required';
      return errors;
    },
    onSubmit: async (values) => {
      if (!item) return;

      const req = {
        records: {
          IndexId: item.IndexId,
          DistId: values.DistId,
          EduDisId: values.EduDisId,
          BlkId: values.BlkId,
          SchlTypeId: values.SchlTypeId,
          MngId: values.MngId,
          CateId: values.CateId,
          CatyId: values.CatyId,
          DisNme: values.DisNme,
          BlkNme: values.BlkNme,
          EduDisNme: values.EduDisNme,
          SchlType: values.SchlType,
          MngNme: values.MngNme,
          CateNme: values.CateNme,
          CatyNme: values.CatyNme,
          Type: values.Type,
          BeoUser: item.BeoUser,
          DeoUser: item.DeoUser,
          AeoUser: values.AeoUser,
          HillRot: values.HillRot ? '1' : '0',
        },
      };

      try {
        const res = await postChangeSch(req).unwrap();
        if (res.dataStatus) {
          alertService.success(res.message ?? 'Updated Successfully');
          setIsEdit(false);
          // Re-fetch the school details after a successful update
          refreshData();
        } else {
          alertService.error(res.message || 'Update Failed');
        }
      } catch {
        alertService.error('Update Failed');
      }
    },
  });

  // Cascading queries driven by formik values
  const { data: eduDistRaw } = useGetEduDistQuery(
    { districtId: schForm.values.DistId },
    { skip: !schForm.values.DistId || !isEdit }
  );
  const { data: blockRaw } = useGetDisBlockQuery(
    { districtId: schForm.values.DistId },
    { skip: !schForm.values.DistId || !isEdit }
  );

  // ── Derived data ─────────────────────────────────────────────────────────

  const schoolDetails = useMemo(() => {
    if (!schRaw?.dataStatus) return [];
    const result = Array.isArray(schRaw.result)
      ? schRaw.result
      : [schRaw.result];
    if (result.length === 0) return [];
    const rec = { ...result[0] };
    const typeMatch = TYPE_LIST.find((t) => t.value === String(rec.Type ?? ''));
    rec.TypeName = typeMatch ? typeMatch.label : '';
    rec.HillRotName = rec.HillRot === '1' || rec.HillRot === 1 ? 'Yes' : 'No';
    return [rec];
  }, [schRaw]);

  // Surface the API failure message once, only when a lookup actually returns
  // an unsuccessful response (not on the initial empty render).
  useEffect(() => {
    if (submittedUdise && schRaw && !schRaw.dataStatus) {
      alertService.error(schRaw.message);
    }
  }, [schRaw, submittedUdise]);

  const item = schoolDetails.length > 0 ? schoolDetails[0] : null;

  // ── Dropdown option lists ────────────────────────────────────────────────

  const DistrictList: Option[] = useMemo(() => {
    if (!districtRaw?.dataStatus) return [];
    return districtRaw.result.map((d: any) => ({
      label: d.district_name ?? d.DisNme,
      value: String(d.id ?? d.district_id ?? d.DistId),
    }));
  }, [districtRaw]);

  const EduDisList: Option[] = useMemo(() => {
    if (!eduDistRaw?.dataStatus) return [];
    return eduDistRaw.result.map((e: any) => ({
      label: e.EduDistNme ?? e.edu_dist_name ?? e.EduDisNme,
      value: String(e.EduDistId ?? e.edu_district_id ?? e.EduDisId),
    }));
  }, [eduDistRaw]);

  const BlockList: Option[] = useMemo(() => {
    if (!blockRaw?.dataStatus) return [];
    return blockRaw.result.map((b: any) => ({
      label: b.BlkNme ?? b.block_name,
      value: String(b.BlkId ?? b.block_id),
    }));
  }, [blockRaw]);

  const ManagementList: Option[] = useMemo(() => {
    if (!managementRaw) return [];
    const list = managementRaw.Management ?? managementRaw;
    if (!Array.isArray(list)) return [];
    return list.map((m: any) => ({ label: m.label, value: String(m.id) }));
  }, [managementRaw]);

  const SchCategoryList: Option[] = useMemo(() => {
    if (!schCategoryRaw) return [];
    const list = schCategoryRaw.SchoolCategory ?? schCategoryRaw;
    if (!Array.isArray(list)) return [];
    return list.map((s: any) => ({
      label: s.label,
      value: String(s.value ?? s.id),
    }));
  }, [schCategoryRaw]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const submit = useCallback(() => {
    if (!udise) {
      alertService.error('Please fill udise code');
      return;
    }
    setIsEdit(false);
    // Clicking Submit drives useGetSchDetailsQuery via submittedUdise.
    // If the same UDISE is already submitted, force a refetch instead.
    if (submittedUdise === udise) {
      refetchSchDetails();
    } else {
      setSubmittedUdise(udise);
    }
    setIsViewSchDet(true);
  }, [udise, submittedUdise, refetchSchDetails]);

  const refreshData = useCallback(() => {
    setIsViewSchDet(true);
    refetchSchDetails();
  }, [refetchSchDetails]);

  /** Angular: handleEdit() — patches form from record */
  const handleEdit = useCallback(() => {
    if (!isEdit && item) {
      schForm.setValues({
        DistId: String(item.DistId ?? ''),
        EduDisId: String(item.EduDisId ?? ''),
        BlkId: String(item.BlkId ?? ''),
        SchlTypeId: String(item.SchlTypeId ?? ''),
        MngId: String(item.MngId ?? ''),
        CateId: String(item.CateId ?? ''),
        CatyId: String(item.CatyId ?? ''),
        Type: String(item.Type ?? ''),
        AeoUser: String(item.AeoUser ?? ''),
        HillRot: item.HillRot === '1' || item.HillRot === 1,
        DisNme: String(item.DisNme ?? ''),
        EduDisNme: String(item.EduDisNme ?? ''),
        BlkNme: String(item.BlkNme ?? ''),
        SchlType: String(item.SchlType ?? ''),
        MngNme: String(item.MngNme ?? ''),
        CateNme: String(item.CateNme ?? ''),
        CatyNme: String(item.CatyNme ?? ''),
      });
      setIsEdit(true);
    } else {
      setIsEdit(false);
      refreshData();
    }
  }, [isEdit, item, refreshData, schForm]);

  const handleCancel = useCallback(() => {
    setIsEdit(false);
    schForm.resetForm();
  }, [schForm]);

  /** Angular: handleDistrict(dis) — cascade reset */
  const handleDistrict = useCallback(
    (value: string, label: string) => {
      schForm.setValues((prev) => ({
        ...prev,
        DistId: value,
        DisNme: label,
        EduDisId: '',
        EduDisNme: '',
        BlkId: '',
        BlkNme: '',
      }));
    },
    [schForm]
  );

  /** Angular: handleEduDis(EduDis) */
  const handleEduDis = useCallback(
    (value: string, label: string) => {
      schForm.setValues((prev) => ({
        ...prev,
        EduDisId: value,
        EduDisNme: label,
        BlkId: '',
        BlkNme: '',
      }));
    },
    [schForm]
  );

  /** handleEditSubmit — validates via Formik, then submits */
  const handleEditSubmit = useCallback(() => {
    schForm.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        alertService.error('Please fill all fields');
        return;
      }
      schForm.handleSubmit();
    });
  }, [schForm]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="main-body-content">
      {/* ── UDISE Search ──────────────────────────────────────────── */}
      <div className="card p-4">
        <div className="form-group row">
          <div className="col-lg-4">
            <b>UDISE CODE</b>
            <InputText
              value={udise}
              onChange={(e) => setUdise(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder="Udise code"
              className="form-control"
              maxLength={11}
              keyfilter="int"
            />
          </div>
          <div className="col-lg-4 mt-4">
            <Button
              type="button"
              icon="fa fa-success"
              className="p-button-success"
              label="Submit"
              onClick={submit}
            />
          </div>
        </div>
      </div>
      <br />

      {/* ── School Details (p-table structure) ────────────────────── */}
      {isViewSchDet && item && (
        <div className="row">
          <div className="col">
            <div className="p-datatable p-component p-datatable-gridlines">
              {/* Caption */}
              <div
                className="p-datatable-header"
                style={{ padding: '0.5rem 1rem' }}
              >
                <div className="row">
                  <div
                    className="col"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    School Details
                  </div>
                  {!isEdit && (
                    <div className="col text-right text-end">
                      <Button
                        type="button"
                        className="p-button-primary"
                        label="Edit"
                        onClick={handleEdit}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Body — manual <tr><td> rows, identical to Angular template */}
              <div className="p-datatable-wrapper">
                <table
                  className="p-datatable-table w-100"
                  role="table"
                  style={{
                    borderCollapse: 'collapse',
                    border: '1px solid #dee2e6',
                  }}
                >
                  <tbody className="p-datatable-tbody">
                    {/* School Name */}
                    <tr>
                      <td>
                        <b>School Name</b>
                      </td>
                      <td>{item.SchlNme}</td>
                    </tr>

                    {/* District */}
                    <tr>
                      <td>
                        <b>District</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.DisNme}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.DistId}
                                options={DistrictList}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select district.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) => {
                                  const lbl =
                                    DistrictList.find(
                                      (o) => o.value === e.value
                                    )?.label ?? '';
                                  handleDistrict(e.value, lbl);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Education District */}
                    <tr>
                      <td>
                        <b>Education District</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.EduDisNme}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.EduDisId}
                                options={EduDisList}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select Education District.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) => {
                                  const lbl =
                                    EduDisList.find((o) => o.value === e.value)
                                      ?.label ?? '';
                                  handleEduDis(e.value, lbl);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Block */}
                    <tr>
                      <td>
                        <b>Block</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.BlkNme}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.BlkId}
                                options={BlockList}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select block.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) => {
                                  const lbl =
                                    BlockList.find((o) => o.value === e.value)
                                      ?.label ?? '';
                                  schForm.setFieldValue('BlkId', e.value);
                                  schForm.setFieldValue('BlkNme', lbl);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* School Category */}
                    <tr>
                      <td>
                        <b>School Category</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.CateNme}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.CateId}
                                options={SchCategoryList}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select category.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) => {
                                  const lbl =
                                    SchCategoryList.find(
                                      (o) => o.value === e.value
                                    )?.label ?? '';
                                  schForm.setFieldValue('CateId', e.value);
                                  schForm.setFieldValue('CateNme', lbl);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* School Type Category */}
                    <tr>
                      <td>
                        <b>School Type Category</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.CatyNme}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.CatyId}
                                options={SCH_TYPE_CATEGORY_LIST}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select category.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) => {
                                  const lbl =
                                    SCH_TYPE_CATEGORY_LIST.find(
                                      (o) => o.value === e.value
                                    )?.label ?? '';
                                  schForm.setFieldValue('CatyId', e.value);
                                  schForm.setFieldValue('CatyNme', lbl);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Management */}
                    <tr>
                      <td>
                        <b>Management</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.MngNme}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.MngId}
                                options={ManagementList}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select management.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) => {
                                  const lbl =
                                    ManagementList.find(
                                      (o) => o.value === e.value
                                    )?.label ?? '';
                                  schForm.setFieldValue('MngId', e.value);
                                  schForm.setFieldValue('MngNme', lbl);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* School Type */}
                    <tr>
                      <td>
                        <b>School Type</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.SchlType}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.SchlTypeId}
                                options={SCHOOL_TYPE_LIST}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select school type.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) => {
                                  const lbl =
                                    SCHOOL_TYPE_LIST.find(
                                      (o) => o.value === e.value
                                    )?.label ?? '';
                                  schForm.setFieldValue('SchlTypeId', e.value);
                                  schForm.setFieldValue('SchlType', lbl);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Type */}
                    <tr>
                      <td>
                        <b>Type</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.TypeName}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <Dropdown
                                value={schForm.values.Type}
                                options={TYPE_LIST}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select type.."
                                filter
                                filterBy="label"
                                appendTo="self"
                                className="w-100"
                                onChange={(e) =>
                                  schForm.setFieldValue('Type', e.value)
                                }
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* AEO ID */}
                    <tr>
                      <td>
                        <b>AEO ID</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.AeoUser}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <InputText
                                value={schForm.values.AeoUser}
                                placeholder="Enter Aeo User.."
                                maxLength={8}
                                keyfilter="int"
                                className="form-control"
                                onChange={(e) =>
                                  schForm.setFieldValue(
                                    'AeoUser',
                                    e.target.value.replace(/\D/g, '')
                                  )
                                }
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Hill Rotation */}
                    <tr>
                      <td>
                        <b>Hill Rotation</b>
                      </td>
                      <td>
                        {!isEdit && <span>{item.HillRotName}</span>}
                        {isEdit && (
                          <div className="row">
                            <div className="col-5">
                              <InputSwitch
                                checked={schForm.values.HillRot}
                                onChange={(e) =>
                                  schForm.setFieldValue('HillRot', e.value)
                                }
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Submit / Cancel row — only in edit mode */}
                    {isEdit && (
                      <tr>
                        <td></td>
                        <td>
                          <Button
                            type="button"
                            icon="fa fa-success"
                            className="p-button-success"
                            label="Submit"
                            onClick={handleEditSubmit}
                          />
                          &nbsp;
                          <Button
                            type="button"
                            icon="fa fa-success"
                            className="p-button-danger"
                            label="Cancel"
                            onClick={handleCancel}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeSchoolInfo;
